/**
 * Checkout Availability Composable
 * Phase 2: Availability Check
 *
 * Encapsulates availability check logic for checkout process.
 * Provides helper functions for UI state management and error handling.
 *
 * Uses shared availability helpers for consistent behavior across the app.
 */

import { computed } from 'vue';
import { useCheckoutStore } from '@/stores/checkout';
import { useAvailabilityHelpers } from '@/composables/availability/useAvailabilityHelpers';
import type { MultiRoomAvailabilityCheckResult } from '@/types/checkout';
import type { AlertType } from '@/composables/availability/useAvailabilityHelpers';

/**
 * Composable for managing checkout availability check
 *
 * @example
 * ```typescript
 * const {
 *   isChecking,
 *   error,
 *   isAvailable,
 *   hasChecked,
 *   canProceed,
 *   checkAvailability,
 *   retry,
 *   getStatusMessage,
 *   getStatusType,
 *   canRetry,
 * } = useCheckoutAvailability();
 *
 * onMounted(async () => {
 *   // After Phase 1 validation passes
 *   if (canProceedPhase1) {
 *     await checkAvailability();
 *   }
 * });
 * ```
 */
export function useCheckoutAvailability() {
  const checkoutStore = useCheckoutStore();
  const availabilityHelpers = useAvailabilityHelpers();

  const isChecking = computed(() => checkoutStore.isCheckingAvailability);
  const error = computed(() => checkoutStore.availabilityError);
  const isAvailable = computed(() => checkoutStore.isAvailable);
  const hasChecked = computed(() => checkoutStore.hasCheckedAvailability);
  const result = computed<MultiRoomAvailabilityCheckResult | null>(
    () => checkoutStore.multiRoomAvailabilityResult
  );
  const code = computed(() => checkoutStore.availabilityCode);
  const canProceed = computed(() => checkoutStore.canProceedToNextStep);
  const sessionId = computed(() => checkoutStore.sessionId);
  const activityId = computed(() => checkoutStore.activityId);

  const bookingFlowType = computed(() => {
    return availabilityHelpers.getBookingFlowType(code.value);
  });

  // Actions
  async function checkAvailability(): Promise<void> {
    await checkoutStore.checkProductAvailability();
  }

  async function retry(): Promise<void> {
    await checkoutStore.retryAvailabilityCheck();
  }

  function clear(): void {
    checkoutStore.clearAvailability();
  }

  function getStatusMessage(): string {
    return availabilityHelpers.getStatusMessage(code.value, error.value);
  }

  function getStatusType(): AlertType {
    return availabilityHelpers.getStatusType(code.value, error.value !== null, isChecking.value);
  }

  function canRetry(): boolean {
    return availabilityHelpers.canRetry(error.value);
  }

  function shouldShowReturnButton(): boolean {
    return availabilityHelpers.shouldShowReturnButton(code.value, error.value, hasChecked.value);
  }

  function getErrorType(): 'timeout' | 'network' | 'unavailable' | 'unknown' | null {
    if (!error.value) return null;

    const errorLower = error.value.toLowerCase();

    if (errorLower.includes('timeout')) {
      return 'timeout';
    }

    if (errorLower.includes('network')) {
      return 'network';
    }

    if (errorLower.includes('not available') || errorLower.includes('unavailable')) {
      return 'unavailable';
    }

    return 'unknown';
  }

  return {
    // State
    isChecking,
    error,
    isAvailable,
    hasChecked,
    result,
    code,
    canProceed,
    sessionId,
    activityId,
    bookingFlowType,

    // Actions
    checkAvailability,
    retry,
    clear,

    // Helpers
    getStatusMessage,
    getStatusType,
    canRetry,
    shouldShowReturnButton,
    getErrorType,
  };
}
